import type { ShellStore } from "@observe-tech/marlin-store/contract";

export const RELEASE_NOTES_URL = "/manual?page=release-notes";

// Desktop version strings carry decoration on both ends (test_release_ prefixes,
// -mfe / -mfe-rc.N suffixes). The changelog keys releases on bare semver and
// discards anything else, so reduce to that before handing it over.
const SEMVER = /(\d+\.\d+\.\d+)/;

/** The changelog URL, focused on a release when the version is recognisable. */
export function releaseNotesUrlFor(version: string): string {
  const match = version.match(SEMVER);
  return match ? `${RELEASE_NOTES_URL}&version=${match[1]}` : RELEASE_NOTES_URL;
}

async function installedVersion(): Promise<string | null> {
  const bridge = (
    window as unknown as {
      electronAPI?: { getAppVersion?: () => Promise<string> };
    }
  ).electronAPI;
  if (!bridge?.getAppVersion) return null;
  try {
    return await bridge.getAppVersion();
  } catch {
    // A broken bridge must never block the app from starting.
    return null;
  }
}

/**
 * Open the manual at this release's notes, once, on the first launch after the
 * desktop app updated.
 *
 * The store holds the last version it prompted for and settles the question in one
 * transaction, so two windows starting together cannot both prompt. Resolves false in
 * a browser (no installed version to compare), on a fresh store, and on every launch
 * after the first one on a given version.
 */
export async function promptReleaseNotesIfUpdated(
  store: ShellStore,
): Promise<boolean> {
  const version = await installedVersion();
  if (!version) return false;

  const updatedTo = await store.settings.consumeReleaseNotesPrompt(version);
  if (!updatedTo) return false;

  // MFE routes are entered by pushing the URL and re-emitting popstate, the
  // same way SidebarItem navigates — the tanstack router owns only
  // shell-owned paths.
  window.history.pushState({}, "", releaseNotesUrlFor(updatedTo));
  window.dispatchEvent(
    new PopStateEvent("popstate", { state: window.history.state }),
  );
  return true;
}
